/**
 * Sidebar v5 — UAT batch 260512 restructure.
 *
 * Quick task 260512-uat-batch (2026-05-12) — Petra's deep UAT pass on
 * staging surfaced gaps the Sidebar v4 (260512-sidebar-production-parity)
 * didn't cover. Sidebar v5 adds:
 *
 *   - PM persona support: PMs (`projectManagerId` on ≥1 project) see
 *     Manage with Approvals expandable scoped to Timesheets / Expenses /
 *     Project Joining (no Time Off — that's HR scope).
 *   - Reports as its own section between Manage and Business (manager OR PM).
 *   - "System" admin-only section at the bottom carrying Cron Jobs +
 *     Permissions (moved out of HR / Manage respectively).
 *   - "My Tasks" in My work — list of tasks across the user's project
 *     assignments; v1 placeholder, full UX TBD after Phase 41-17 board
 *     UAT (memory `v7_my_tasks_uat_pending.md`). MKS-477: the same
 *     entry reads "Tasks" and opens /tasks (My Tasks | All tasks tabs).
 *   - "Důležité info" removed from HR (page never built).
 *   - "Task Management" removed from Manage (replaced by personal-scope
 *     My Tasks).
 *   - "Project Templates" relabeled to "Board Templates" so the kanban-
 *     column template page (`/admin/project-templates`) doesn't collide
 *     with the Phase 35 PaymentType / ProjectType / TaskType registry at
 *     `/admin/templates`.
 *   - Role-conditional sidebar project clicks: admin/PM → project detail,
 *     plain user/member → board.
 *
 * Section order (top → bottom):
 *
 *   1. My work    — Dashboard / Timesheets / Time Off / Expenses /
 *                   My Tasks / My Invoices (freelance only)
 *   2. Projects   — Customer→Project hierarchy
 *   3. Manage     — Approvals (expandable) / My Projects / + admin-only
 *                   Users / External Users / Board Templates / Settings
 *                   (gate: effectiveManager OR effectiveAdmin OR isPM)
 *   4. Reports    — Reports (gate: effectiveManager OR isPM)
 *   5. Business   — Leads / Web Analytics (gate: isSales OR isAdmin OR isMarketing)
 *   6. Finance    — Invoices / Payroll / Freelancer Invoices /
 *                   Employee Extra Costs / Company Extra Costs
 *                   (gate: effectiveFinance — already admin OR'd in useAuth)
 *   7. ISMS       — App Access Registry (gate: effectiveISMSManager)
 *   8. HR         — Team Events / Capacity planning (always visible to
 *                   authenticated users)
 *   9. System     — Cron Jobs / Permissions (gate: effectiveAdmin)
 *
 * Impersonation guard (Phase 27 D-13 / Phase 41 Pitfall 5 / checker I-08)
 * preserved verbatim — every admin-sensitive flag is AND'd with
 * !isImpersonating in `effectiveFlags` BEFORE feeding into visibility
 * checks. Verified in Sidebar v5 spec suite.
 */
import { useEffect, useState, type ComponentType, type ComponentProps } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../services/authContext';
import { useModules } from '../../services/modulesContext';
import { useFlag } from '../../services/flagsContext';
import { useApprovalCounts, type ApprovalCounts } from '../../hooks/useApprovalCounts';
import { useNotifications } from '../../hooks/useNotifications';
import { useTeamEventPendingCount } from '../../hooks/useTeamEvents';
import { useMyTrainings } from '../../hooks/useTrainings';
import { useSidebarCustomers } from '../../hooks/useSidebarCustomers';
import { SidebarProjectsSection } from './SidebarProjectsSection';
import { ThemeEmoji } from './ThemeEmoji';
import { UserMenu } from '../layout/UserMenu';
import { useOptionalSidebar } from '../layout/SidebarContext';
// MKS-377 — collapse / expand toggle icons. Not in the curated `./icons` barrel.
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline';
// MKS-538 — the mobile drawer's close button. Not in the curated `./icons` barrel;
// imported directly, mirroring the precedents below.
import { XMarkIcon } from '@heroicons/react/24/outline';
// Phase 62 Plan 07 — To Be Invoiced Finance sidebar nav item (Surface 4).
// ClipboardDocumentCheckIcon confirmed in @heroicons/react 2.2.0 (Assumption A3).
import { ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
// Phase 99 (Plan 99-01) — Data Import sidebar nav item. Not in the curated
// `./icons` barrel; imported directly, mirroring the ClipboardDocumentCheckIcon
// precedent above.
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
// Phase 107 (Plan 107-11) — SLA Pricing sidebar nav icon (D-01). Not in the
// curated `./icons` barrel; imported directly, mirroring the two precedents
// above. Deliberately distinct from CurrencyDollarIcon, already used by
// Extra Costs.
import { CalculatorIcon } from '@heroicons/react/24/outline';
// MKS-339 — Helpdesk nav items (My work + Manage). Not in the curated
// `./icons` barrel; imported directly, mirroring the precedents above.
import { LifebuoyIcon } from '@heroicons/react/24/outline';
import {
  HomeIcon,
  ClockIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  CheckCircleIcon,
  FolderIcon,
  UserIcon,
  UsersIcon,
  UserPlusIcon,
  LockClosedIcon,
  ChartBarIcon,
  CogIcon,
  DocumentTextIcon,
  KeyIcon,
  ShieldCheckIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  BriefcaseIcon,
  PresentationChartLineIcon,
  RectangleStackIcon,
  ChartPieIcon,
  ChevronDownIcon,
  // Phase 106 — Fleet Console nav icon.
  ServerStackIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  BellIcon,
  ClipboardDocumentListIcon,
  // MKS-393 — the HR merch overview nav icon.
  GiftIcon,
  HandRaisedIcon,
  AcademicCapIcon,
  ExclamationTriangleIcon,
  // Phase 115 (Plan 115-06) — Robot Tasks dashboard nav icon.
  BoltIcon,
  // MKS-344 — Sales › Contacts nav icon.
  IdentificationIcon,
  // MKS-447 (part 6) — Linear mirror reconciliation nav icon.
  ArrowsRightLeftIcon,
} from './icons';

type IconCmp = ComponentType<ComponentProps<'svg'> & { title?: string }>;

interface NavItem {
  label: string;
  href: string;
  badgeCount?: number;
  icon?: IconCmp;
  /** Optional per-item visibility predicate. Section visibility is checked
      first; this filters inside an already-visible section. */
  visible?: boolean;
  /** When true, the item is "active" only on an exact pathname match (not a
      prefix). Used for hrefs that are a prefix of other nav items' hrefs. */
  exact?: boolean;
}

function Badge({ count, compact = false }: { count: number; compact?: boolean }) {
  if (count <= 0) return null;
  if (compact) {
    // MKS-377 — the icon rail: a small count pinned to the icon's top-right corner.
    return (
      <span className="absolute -top-0.5 right-0.5 bg-rose-500 text-white text-[9px] leading-none rounded-full min-w-[1rem] h-4 flex items-center justify-center px-0.5">
        {count > 99 ? '99+' : count}
      </span>
    );
  }
  return (
    <span className="bg-rose-500 text-white text-[10px] rounded-full min-w-[1.25rem] h-5 flex items-center justify-center px-1">
      {count > 99 ? '99+' : count}
    </span>
  );
}

/**
 * MKS-538 — is the viewport at or above Tailwind's `lg` breakpoint (1024px)?
 *
 * The drawer's position is CSS (`fixed` below `lg`, `lg:static`), but the
 * sidebar's CONTENT shape is JavaScript: `collapsed` renders an icon rail
 * instead of labelled rows, and a 240px-wide rail inside the drawer would be
 * nonsense. So below `lg` the collapsed state is ignored and the drawer always
 * shows the full menu.
 *
 * Mirrors the `window.matchMedia` guard in `hooks/useTheme.ts`: where the API
 * is missing (older jsdom in a component test) this reports desktop, which is
 * the behaviour every caller had before this flag existed.
 */
const DESKTOP_QUERY = '(min-width: 1024px)';

function useIsDesktop(enabled: boolean): boolean {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window.matchMedia === 'function' ? window.matchMedia(DESKTOP_QUERY).matches : true
  );

  useEffect(() => {
    if (!enabled || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(DESKTOP_QUERY);
    setIsDesktop(mql.matches);
    const listener = (event: { matches: boolean }) => setIsDesktop(event.matches);
    mql.addEventListener('change', listener);
    return () => mql.removeEventListener('change', listener);
  }, [enabled]);

  return enabled ? isDesktop : true;
}

function SectionHeader({ label, href }: { label: string; href?: string }) {
  // Phase 45 Plan 45-05 (D-09) — Projects section header gains a clickable
  // nav target → /manager/overview for managers + admins. Members keep the
  // collapse-only behavior because no href is passed at the Projects call
  // site. All other section headers (My work, Manage, Reports, Business,
  // Finance, ISMS, HR, System) pass no `href` and render as a plain <div>;
  // the Phase 41 P15 visual contract is preserved verbatim (className
  // unchanged across both branches).
  const baseClass = 'px-2 mb-1 text-[10px] font-bold uppercase tracking-wider text-ms-text-muted';
  if (href) {
    return (
      <Link
        to={href}
        className={`${baseClass} block hover:text-ms-text-strong focus:outline-none focus:ring-2 focus:ring-ms-orange/40 rounded-sm`}
        aria-label={`Go to ${label} overview`}
      >
        {label}
      </Link>
    );
  }
  return <div className={baseClass}>{label}</div>;
}

export function Sidebar() {
  const auth = useAuth();
  const location = useLocation();

  // Phase 93 (Plan 93-02) — nav-level module gating, composed onto the
  // existing NavItem.visible mechanism below. Presentation only: hiding a
  // link grants nothing — a disabled module's Fastify plugins are never
  // registered server-side, so its URL still 404s regardless of what this
  // component renders (docs/private/MODULES.md, T-93-07).
  const { hasModule } = useModules();

  // MKS-339 — the two Helpdesk nav items appear with the feature. `useFlag`
  // is fail-closed (off while loading, on error, and with no provider), so
  // the worst case is the entries arriving a moment late rather than a link
  // into a page that renders a placeholder.
  const helpdeskOn = useFlag('mks_339_helpdesk_inbox');

  // MKS-377 — the collapsible icon rail. The state lives in SidebarContext (persisted per browser);
  // without a provider the sidebar is always expanded with no toggle.
  const sidebarState = useOptionalSidebar();
  const canCollapse = sidebarState !== null;

  // MKS-538 — below `lg` the sidebar left the content a ~135px column on an
  // iPhone instead of overlaying it, so every card's text spilled out of its
  // box. With the flag on it becomes an off-canvas drawer: out of flow (so the
  // page keeps the full width), slid in over a backdrop when `mobileOpen`, and
  // opened from the `AppHeader` button. At `lg` and above nothing changes.
  const mobileDrawerOn = useFlag('mks_538_mobile_sidebar_drawer');
  const isDesktop = useIsDesktop(mobileDrawerOn);
  const mobileOpen = Boolean(mobileDrawerOn && sidebarState?.mobileOpen);
  // The setter from `useState` is stable, so effects can depend on it; the
  // context VALUE object is rebuilt on every provider render and cannot be.
  const setMobileOpen = sidebarState?.setMobileOpen;

  const collapsed = canCollapse && sidebarState.isCollapsed && isDesktop;
  const toggleCollapsed = () => sidebarState?.toggle();

  // Close the drawer when the route changes — otherwise tapping a nav item
  // leaves it covering the page it just opened (verbatim from
  // ExternalNavigation.tsx, the portal's drawer).
  useEffect(() => {
    if (!mobileDrawerOn) return;
    setMobileOpen?.(false);
  }, [mobileDrawerOn, location.pathname, setMobileOpen]);

  // Escape closes it, like every other overlay in the app.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen?.(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen, setMobileOpen]);

  // Impersonation-aware flags. Admin-sensitive surfaces must be hidden
  // while the admin is impersonating a non-admin user.
  const effectiveFlags = {
    isAdmin: auth.isAdmin && !auth.isImpersonating,
    isManager: auth.isManager && !auth.isImpersonating,
    canManageTasks: auth.canManageTasks && !auth.isImpersonating,
    isFinance: auth.isFinance && !auth.isImpersonating,
    // quick-260813-hob: strict finance-only, same !isImpersonating treatment
    // as every sibling flag. Used ONLY to gate the Monthly Salaries item —
    // the Finance SECTION stays on the loose `isFinance` above.
    isFinanceOnly: auth.isFinanceOnly && !auth.isImpersonating,
    isSales: auth.isSales && !auth.isImpersonating,
    isMarketing: auth.isMarketing && !auth.isImpersonating,
    isISMSManager: auth.isISMSManager && !auth.isImpersonating,
    // MKS-393 — admin + hr, the audience of the merch overview. Same
    // !isImpersonating treatment as every sibling flag.
    isHR: auth.isHR && !auth.isImpersonating,
    // Phase 106 / D-01 / D-09 — no `isAdmin` fallback: the narrower
    // superadmin gate must not be widened to every admin. Same
    // !isImpersonating treatment as every sibling flag.
    isSuperadmin: auth.isSuperadmin && !auth.isImpersonating,
  };

  // UAT 2026-05-13 F-D5 — case-insensitive + trim match against common
  // variants ('Freelance', 'Freelancer'). Petra reported "My Invoices"
  // missing despite being freelance; root cause was a string-literal
  // mismatch ('Freelance' vs 'Freelancer'/casing). Backend canonical name
  // remains 'Freelance' per freelancerInvoiceController.ts; UI now tolerates
  // small drifts so the gate doesn't quietly hide the link.
  const employeeTypeName = (auth.user?.employeeType?.name ?? '').trim().toLowerCase();
  const isFreelance = employeeTypeName === 'freelance' || employeeTypeName === 'freelancer';

  // F-22 (2026-05-13 UAT batch Phase 3) — PM persona detection now comes
  // from `auth.isPM` (backed by GET /api/auth/me `isPM` boolean). The prior
  // `useMyProjects().some(p => p.projectManager?.id === auth.user?.id)`
  // pattern was broken because the memberOf=true response branch nulls
  // `projectManager` on sidebar rows AND hard-filters state='in_progress' —
  // so a PM of a paused/completed project would never light up. The
  // !isImpersonating gate is already baked into `auth.isPM` at the context
  // level (see authContext.tsx), so no inline AND needed here.
  const isPM = auth.isPM;

  // Approvals counts pulled when any approval-gate user is present so the
  // expandable parent can show the rolled-up total badge. Additionally gated
  // on the approval-owning modules (WR-01): on an instance where time,
  // billing AND projects are all disabled, every Approvals child is
  // module-hidden, so a manager should not poll /api/approval-counts forever.
  const wantApprovals =
    (effectiveFlags.isManager || effectiveFlags.isAdmin || isPM) &&
    (hasModule('time') || hasModule('billing') || hasModule('projects'));
  const { data: counts } = useApprovalCounts(wantApprovals);

  // UAT 2026-05-13 — Sidebar Notifications entry shows a badge for unread
  // count so PMs see SLA threshold / breach alerts (Phase 39 dispatch) at a
  // glance. Hook polls every 60s, matching useApprovalCounts cadence.
  const { data: notificationsData } = useNotifications({ unread: true, limit: 1 });
  const unreadNotificationsCount = notificationsData?.unreadCount ?? 0;
  // Module-gated badge fetches (WR-01): the pending-count endpoint is
  // people-owned and the trainings endpoint is isms-owned — on an instance
  // with those modules disabled the backend plugins aren't registered, so an
  // ungated fetch is a guaranteed 404. Fail-open still holds: while modules
  // are loading, `hasModule` returns true, so default instances are unchanged.
  const { data: pendingEventCount } = useTeamEventPendingCount(hasModule('people'));
  // My Work › Trainings badge — count of the current user's trainings that are
  // overdue or due-soon (matches "due or overdue"). Shares the useMyTrainings
  // cache with the My Trainings page, so it refreshes when that page is visited.
  const { data: myTrainings } = useMyTrainings(hasModule('isms'));
  const trainingAttentionCount = (myTrainings ?? []).filter(
    (t) => t.state === 'overdue' || t.dueSoon
  ).length;
  const {
    data: customers,
    isLoading: customersLoading,
    isError: customersError,
  } = useSidebarCustomers();

  const approvalCount = (key: keyof ApprovalCounts): number => (counts && counts[key]) ?? 0;

  // MKS-372 — the "ready for Time & Material" list appears with the feature.
  // Fail-closed like every `useFlag`: off, the entry is simply not there.
  const tmConversionOn = useFlag('mks_372_tm_conversion_check');

  // Phase 113 (MKS-208) — the backdated-hours queue follows the module gate
  // and the admin/PM persona the sibling join-request row uses.
  const backdatedRequestsVisible = (effectiveFlags.isAdmin || isPM) && hasModule('projects');

  // Total rolls up everything the actor is allowed to act on. Each term is
  // additionally gated on its owning module (D-6) so a disabled module's
  // pending-work count never contributes to the badge (docs/private/MODULES.md:149).
  const totalApprovals =
    ((effectiveFlags.isManager || isPM) && hasModule('time')
      ? approvalCount('managerTimesheets')
      : 0) +
    ((effectiveFlags.isManager || isPM) && hasModule('billing')
      ? approvalCount('managerExpenses')
      : 0) +
    (effectiveFlags.isManager && hasModule('time') ? approvalCount('managerTimeOffs') : 0) +
    ((effectiveFlags.isAdmin || isPM) && hasModule('projects')
      ? approvalCount('adminJoinRequests')
      : 0) +
    // Phase 113 (MKS-208, D-11) — pending backdated-hours requests. No email
    // and no Slack in v1; this badge is the whole notification.
    (backdatedRequestsVisible ? approvalCount('adminBackdatedRequests') : 0);

  const isApprovalsRouteActive =
    location.pathname.startsWith('/manager/timesheets/pending') ||
    location.pathname.startsWith('/manager/expenses/pending') ||
    location.pathname.startsWith('/manager/time-off/pending') ||
    location.pathname.startsWith('/admin/join-requests') ||
    location.pathname.startsWith('/admin/backdated-hours-requests');

  const [approvalsOpen, setApprovalsOpen] = useState(isApprovalsRouteActive);

  // Petra UAT 2026-05-22 — "Manager Overview" surfaces as an explicit item at
  // the top of the Projects section for PM + admin (was previously a header
  // link). Impersonating admin → effectiveFlags.isAdmin AND .isManager both
  // false (masked by !isImpersonating in effectiveFlags) → link does not
  // render, Phase 41 P15 impersonation guard preserved.
  // `/manager/overview` is workmgmt-owned, so it's additionally gated on
  // `hasModule('workmgmt')` (93-02 nav map).
  const showManagerOverviewLink =
    (effectiveFlags.isAdmin || effectiveFlags.isManager || isPM) && hasModule('workmgmt');

  // ─── Item lists ─────────────────────────────────────────────────────────

  const myWorkItems: NavItem[] = [
    { label: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { label: 'Timesheets', href: '/time', icon: ClockIcon, visible: hasModule('time') },
    { label: 'Time Off', href: '/time-off', icon: CalendarDaysIcon, visible: hasModule('time') },
    {
      label: 'Expenses',
      href: '/expenses',
      icon: CreditCardIcon,
      visible: hasModule('billing'),
    },
    {
      label: 'Tasks',
      href: '/tasks',
      icon: Squares2X2Icon,
      visible: hasModule('workmgmt'),
    },
    // 260727-gob — no role gate; module-gated on workmgmt since Phase 93.
    // MKS-410 — same entry, same POSITION, new name behind the helpdesk flag:
    // the page it opens stops being the Report-an-issue board directory and
    // becomes the one "New request" form plus "My requests". The flag-off
    // label stays "Report an issue" because the flag-off page still is one.
    {
      label: helpdeskOn ? 'Helpdesk' : 'Report an issue',
      href: '/report-issue',
      icon: ExclamationTriangleIcon,
      visible: hasModule('workmgmt'),
    },
    // MKS-339 — the cross-project client-request inbox. No role gate: the
    // backend scopes the list to the caller's own intake-enabled projects and
    // someone with none sees an empty inbox, so a role gate here would only
    // hide the page from project members who DO handle requests. Flag-gated so
    // the entry appears with the feature.
    //
    // MKS-410 renames it "Helpdesk triage": the entry above is now the one
    // called Helpdesk, and this is the handling side of it — an admin's
    // unified queue, a project member's client inbox. Two entries both reading
    // "Helpdesk" would say nothing about which is which.
    {
      label: 'Helpdesk triage',
      href: '/helpdesk',
      icon: LifebuoyIcon,
      visible: hasModule('workmgmt') && helpdeskOn,
    },
    // Training compliance (mockup) — every user sees their required trainings,
    // status, and a deep link into the Dojo test. Admin config lives under Manage.
    {
      label: 'Trainings',
      href: '/trainings',
      icon: AcademicCapIcon,
      badgeCount: trainingAttentionCount,
      visible: hasModule('isms'),
    },
    // Phase 61 follow-up (Option C) — peer-to-peer kudos shortcut.
    // No /profile/:id route exists, so the give-kudos modal is opened
    // from this sidebar action with an inline recipient directory picker.
    {
      label: 'Give kudos',
      href: '/give-kudos',
      icon: HandRaisedIcon,
      visible: hasModule('people'),
    },
    {
      label: 'My Invoices',
      href: '/my-invoices',
      icon: DocumentTextIcon,
      // D-2 — payroll, not billing.
      visible: hasModule('payroll') && isFreelance,
    },
  ];

  // Approvals expandable — sub-items render only when the actor has the
  // matching responsibility. PM (non-manager) sees Timesheets / Expenses /
  // Project Joining; Time Off stays manager-only (HR scope).
  const approvalsChildren: NavItem[] = [
    {
      label: 'Timesheets',
      href: '/manager/timesheets/pending',
      icon: ClockIcon,
      badgeCount: approvalCount('managerTimesheets'),
      visible: (effectiveFlags.isManager || isPM) && hasModule('time'),
    },
    {
      label: 'Expenses',
      href: '/manager/expenses/pending',
      icon: CreditCardIcon,
      badgeCount: approvalCount('managerExpenses'),
      visible: (effectiveFlags.isManager || isPM) && hasModule('billing'),
    },
    {
      label: 'Time Off',
      href: '/manager/time-off/pending',
      icon: CalendarDaysIcon,
      badgeCount: approvalCount('managerTimeOffs'),
      visible: effectiveFlags.isManager && hasModule('time'),
    },
    {
      label: 'Project Joining',
      href: '/admin/join-requests',
      icon: UserPlusIcon,
      badgeCount: approvalCount('adminJoinRequests'),
      visible: (effectiveFlags.isAdmin || isPM) && hasModule('projects'),
    },
    {
      label: 'Backdated Hours',
      href: '/admin/backdated-hours-requests',
      icon: LockClosedIcon,
      badgeCount: approvalCount('adminBackdatedRequests'),
      visible: backdatedRequestsVisible,
    },
  ];

  // D-6 — the Approvals expandable itself (button + children container)
  // should not render at all when every child is module-hidden, so a
  // core-only instance shows no orphan Approvals row.
  const approvalsAnyVisible = approvalsChildren.some((item) => item.visible !== false);

  const manageItems: NavItem[] = [
    {
      label: 'Notifications',
      href: '/notifications',
      icon: BellIcon,
      badgeCount: unreadNotificationsCount,
    },
    { label: 'My Projects', href: '/projects', icon: FolderIcon, visible: hasModule('projects') },
    {
      label: 'Task Management',
      href: '/manager/tasks',
      icon: ClipboardDocumentListIcon,
      visible: hasModule('workmgmt'),
    },
    {
      label: 'Users',
      href: '/admin/users',
      icon: UsersIcon,
      visible: effectiveFlags.isAdmin,
    },
    {
      label: 'Data Import',
      href: '/admin/imports',
      icon: ArrowUpTrayIcon,
      visible: effectiveFlags.isAdmin,
    },
    {
      label: 'External Users',
      href: '/admin/external-users',
      icon: UserIcon,
      visible: effectiveFlags.isAdmin && hasModule('portal'),
    },
    {
      label: 'Board Templates',
      href: '/admin/project-templates',
      icon: RectangleStackIcon,
      visible: effectiveFlags.isAdmin && hasModule('projects'),
    },
    // MKS-339 — helpdesk configuration: per-project request categories and
    // canned replies. Admin OR PM, matching the route's own gate (a PM
    // configures their own project's categories).
    {
      label: 'Helpdesk Settings',
      href: '/admin/helpdesk',
      icon: LifebuoyIcon,
      visible:
        (effectiveFlags.isAdmin || isPM) && hasModule('workmgmt') && helpdeskOn,
    },
    {
      label: 'Notification Routes',
      href: '/admin/notifications/routes',
      icon: BellIcon,
      visible: effectiveFlags.isAdmin,
    },
    {
      label: 'Notifications Audit',
      href: '/admin/notifications-audit',
      icon: BellIcon,
      visible: effectiveFlags.isAdmin,
    },
    // Admin entry point to the full timesheet ledger — lists every user's
    // timesheet across all states (open/submitted/approved/rejected) for a
    // period and drills into the complete timesheet detail. The page already
    // existed at /admin/timesheets/pending (AdminPendingTimesheetsPage) but had
    // no sidebar link, leaving a pure admin with no clickable way to reach it.
    {
      label: 'All Timesheets',
      href: '/admin/timesheets/pending',
      icon: ClockIcon,
      visible: effectiveFlags.isAdmin && hasModule('time'),
    },
    // Phase 48 (TS-EDIT-05) — Petra UAT 2026-05-27 added sidebar entry so the
    // admin can reach the orphan-role-entry list without typing the URL.
    {
      label: 'Entries Needing Role',
      href: '/admin/entries-needing-role',
      icon: ClockIcon,
      visible: effectiveFlags.isAdmin && hasModule('time'),
    },
    // MKS-372 — the pre-conversion readiness list over every Default-payment
    // project, so the admin can work through them without typing the URL.
    {
      label: 'Ready for T&M',
      href: '/admin/tm-conversion-readiness',
      icon: ClockIcon,
      visible: tmConversionOn && effectiveFlags.isAdmin && hasModule('billing'),
    },
    {
      label: 'Training',
      href: '/admin/trainings',
      icon: AcademicCapIcon,
      visible: effectiveFlags.isAdmin && hasModule('isms'),
    },
    {
      label: 'Settings',
      href: '/admin',
      icon: CogIcon,
      visible: effectiveFlags.isAdmin,
      exact: true,
    },
  ];

  const reportsItems: NavItem[] = [
    {
      label: 'Reports',
      href: '/reports',
      icon: PresentationChartLineIcon,
      // The landing page itself is a core shell (not route-gated, D-4); every
      // tile behind it belongs to analytics or payroll, so the *link* is
      // hidden only when both are off. Per-tile curation stays deferred.
      visible: hasModule('analytics') || hasModule('payroll'),
    },
  ];

  const businessItems: NavItem[] = [
    { label: 'Leads', href: '/sales/leads', icon: BriefcaseIcon, visible: hasModule('sales') },
    // MKS-344 — the reusable contacts list.
    {
      label: 'Contacts',
      href: '/sales/contacts',
      icon: IdentificationIcon,
      visible: hasModule('sales'),
    },
    {
      label: 'Web Analytics',
      href: '/reports/web-analytics',
      icon: ChartPieIcon,
      visible: hasModule('analytics'),
    },
    // Phase 107 (Plan 107-11) / D-01 — deliberately NARROWER than
    // businessVisible below (which also admits marketing for Leads/Web
    // Analytics): package pricing is commercial-sensitive, so marketing
    // sees the Business section header via those two items but not this
    // entry. Same shape as the Fleet Console item in systemItems (a gate
    // narrower than its section). The backend enforces the identical
    // sales-or-admin gate independently via requireSales on every
    // /api/sla-pricing route (Plan 107-05) — this predicate is UX only.
    {
      label: 'SLA Pricing',
      href: '/sales/sla-pricing',
      icon: CalculatorIcon,
      visible: hasModule('sales') && (effectiveFlags.isSales || effectiveFlags.isAdmin),
    },
  ];

  const financeItems: NavItem[] = [
    // Phase 62 Plan 07 (TBI-06) — first entry per UI-SPEC Surface 4.
    {
      label: 'To Be Invoiced',
      href: '/invoices/pending',
      icon: ClipboardDocumentCheckIcon,
      visible: hasModule('billing'),
    },
    {
      label: 'Invoices',
      href: '/invoices',
      icon: DocumentTextIcon,
      visible: hasModule('billing'),
    },
    // MKS-334 — who owes us what, and for how long.
    {
      label: 'Receivables',
      href: '/invoices/receivables',
      icon: BanknotesIcon,
      visible: hasModule('billing'),
    },
    {
      label: 'Payroll',
      href: '/reports/payroll',
      icon: BanknotesIcon,
      visible: hasModule('payroll'),
    },
    {
      label: 'Freelancer Invoices',
      href: '/reports/freelancer-invoices',
      icon: DocumentTextIcon,
      visible: hasModule('payroll'),
    },
    {
      label: 'Monthly Salaries',
      href: '/admin/monthly-salaries',
      icon: BanknotesIcon,
      // quick-260813-hob: the backend route 403s for a non-finance admin, so
      // showing this link to them would just lead to an error page. The
      // Finance SECTION itself stays visible (financeVisible / isFinance) —
      // only this one item is gated on the strict isFinanceOnly flag.
      visible: hasModule('payroll') && effectiveFlags.isFinanceOnly,
    },
    {
      label: 'Employee Extra Costs',
      href: '/admin/employee-extra-costs',
      icon: CurrencyDollarIcon,
      // Pitfall 2 — the payroll one.
      visible: hasModule('payroll'),
    },
    {
      label: 'Company Extra Costs',
      href: '/admin/extra-costs',
      icon: CurrencyDollarIcon,
      // Pitfall 2 — the billing one.
      visible: hasModule('billing'),
    },
  ];

  const ismsItems: NavItem[] = [
    {
      label: 'App Access Registry',
      href: '/admin/app-access',
      icon: ShieldCheckIcon,
      visible: hasModule('isms'),
    },
  ];

  const hrItems: NavItem[] = [
    {
      label: 'Team Events',
      href: '/hr/team-events',
      icon: CalendarDaysIcon,
      badgeCount: pendingEventCount ?? 0,
      visible: hasModule('people'),
    },
    {
      label: 'Onboarding Steps',
      href: '/hr/onboarding-steps',
      icon: ClipboardDocumentListIcon,
      visible: hasModule('people'),
    },
    {
      label: 'Merch sizes',
      href: '/hr/merch-sizes',
      icon: GiftIcon,
      // The page is HRRoute-guarded, so showing the link to a plain employee
      // would only lead to a redirect. Both halves of the gate are here.
      visible: hasModule('people') && effectiveFlags.isHR,
    },
    {
      label: 'Capacity planning',
      href: '/capacity/planning',
      icon: ChartBarIcon,
      // Backend `resolveCapacityScope` 403s plain employees; admin and PM
      // are the visible audience per product decision. Manager + HR keep
      // backend access (no 403) but the sidebar link surfaces only the
      // primary personas to keep menu noise down for HR/manager who do
      // not actively manage capacity day-to-day.
      visible: hasModule('capacity') && (effectiveFlags.isAdmin || isPM),
    },
  ];

  const systemItems: NavItem[] = [
    {
      label: 'Cron Jobs',
      href: '/admin/cron-jobs',
      icon: ClockIcon,
      visible: effectiveFlags.isAdmin,
    },
    {
      // MKS-447 (part 6) — per-issue, per-field proof that the Linear mirror
      // is complete. Admin + `workmgmt`, the module its endpoint lives in.
      label: 'Linear Mirror',
      href: '/admin/linear-mirror',
      icon: ArrowsRightLeftIcon,
      visible: effectiveFlags.isAdmin && hasModule('workmgmt'),
    },
    {
      // MKS-469 — which project each Linear team (and Linear label) mirrors
      // into. Admin + `workmgmt`.
      label: 'Linear Team Routing',
      href: '/admin/linear-team-routing',
      icon: RectangleStackIcon,
      visible: effectiveFlags.isAdmin && hasModule('workmgmt'),
    },
    {
      label: 'Respect Config',
      href: '/admin/respect',
      icon: CogIcon,
      visible: effectiveFlags.isAdmin && hasModule('people'),
    },
    {
      label: 'Permissions',
      href: '/admin/permissions',
      icon: KeyIcon,
      visible: effectiveFlags.isAdmin,
    },
    {
      label: 'Monk Bot Admin',
      href: '/admin/bot',
      icon: ShieldCheckIcon,
      visible: effectiveFlags.isAdmin && hasModule('monka'),
    },
    {
      // Phase 115 (Plan 115-06 / PCOPS-02) — the executor-aware operator
      // dashboard: every robot-involved task across both executors, with
      // audited cancel/retry. Same admin + monka gate as its sibling above.
      label: 'Factory™ Tasks',
      href: '/admin/robot-tasks',
      icon: BoltIcon,
      visible: effectiveFlags.isAdmin && hasModule('monka'),
    },
    {
      // Phase 106 / D-01 / D-02 / P-28 — no `hasModule(...)` term because the
      // backing routes (`/api/fleet/*`) live in the always-on `core` module
      // and there is no `fleet` module; no `isAdmin` fallback because D-01
      // requires a gate strictly narrower than admin.
      label: 'Fleet Console',
      href: '/admin/fleet',
      icon: ServerStackIcon,
      visible: effectiveFlags.isSuperadmin,
    },
  ];

  // ─── Section visibility ─────────────────────────────────────────────────

  const manageVisible = effectiveFlags.isManager || effectiveFlags.isAdmin || isPM;
  const reportsVisible = effectiveFlags.isManager || isPM;
  const businessVisible =
    effectiveFlags.isSales || effectiveFlags.isAdmin || effectiveFlags.isMarketing;
  const financeVisible = effectiveFlags.isFinance; // already OR'd with admin via useAuth
  const ismsVisible = effectiveFlags.isISMSManager;
  // Phase 106 — a superadmin who is not a tenant admin must still see the
  // System section header to reach Fleet Console; every other item in
  // systemItems stays admin-gated individually.
  const systemVisible = effectiveFlags.isAdmin || effectiveFlags.isSuperadmin;

  // ─── Render helpers ─────────────────────────────────────────────────────

  // `exact` items only light up on an exact pathname match. Needed for the
  // Settings item (href '/admin'), which is a prefix of every other admin
  // route (/admin/trainings, /admin/entries-needing-role, /admin/join-requests…)
  // and would otherwise show as active on all of them.
  const isItemActive = (href: string, exact = false): boolean =>
    exact
      ? location.pathname === href
      : location.pathname === href || location.pathname.startsWith(`${href}/`);

  const itemClass = (active: boolean): string =>
    `flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] font-medium ${
      active
        ? 'bg-ms-orange/10 text-ms-orange-dark'
        : 'text-ms-text-secondary hover:bg-ms-surface-2'
    }`;

  const railItemClass = (active: boolean): string =>
    `relative flex items-center justify-center h-8 w-full rounded-md ${
      active
        ? 'bg-ms-orange/10 text-ms-orange-dark'
        : 'text-ms-text-secondary hover:bg-ms-surface-2'
    }`;

  const renderItem = (item: NavItem) => {
    if (item.visible === false) return null;
    const Icon = item.icon;
    if (collapsed) {
      const badge = item.badgeCount ?? 0;
      return (
        <Link
          key={item.href}
          to={item.href}
          className={railItemClass(isItemActive(item.href, item.exact))}
          title={badge > 0 ? `${item.label} (${badge})` : item.label}
          aria-label={item.label}
        >
          {Icon ? (
            <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
          ) : (
            <span className="text-[11px] font-semibold" aria-hidden="true">
              {item.label.charAt(0)}
            </span>
          )}
          <Badge count={badge} compact />
        </Link>
      );
    }
    return (
      <Link
        key={item.href}
        to={item.href}
        className={itemClass(isItemActive(item.href, item.exact))}
      >
        {Icon && <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />}
        <span className="flex-1 truncate">{item.label}</span>
        <Badge count={item.badgeCount ?? 0} />
      </Link>
    );
  };

  // MKS-377 — in the icon rail a section heading becomes a thin divider.
  const railDivider = <div className="mx-1 mb-1 border-t border-ms-border" aria-hidden="true" />;

  const renderSection = (label: string, items: NavItem[]) => {
    const visibleItems = items.filter((item) => item.visible !== false);
    if (visibleItems.length === 0) return null;
    if (collapsed) {
      return (
        <div className="px-2 py-1" role="group" aria-label={label}>
          {railDivider}
          {visibleItems.map(renderItem)}
        </div>
      );
    }
    return (
      <div className="px-3 py-2">
        <SectionHeader label={label} />
        {visibleItems.map(renderItem)}
      </div>
    );
  };

  // MKS-538 — flag off: the aside is the in-flow column it has always been.
  // Flag on: below `lg` it is a fixed drawer of full menu width, slid out of
  // view until opened; from `lg` up `lg:static` + `lg:w-*` restore exactly the
  // off-branch geometry.
  const asideLayoutClass = mobileDrawerOn
    ? `fixed inset-y-0 left-0 z-40 w-60 shadow-xl transition-transform duration-200 ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:static lg:z-auto lg:shadow-none lg:translate-x-0 lg:transition-[width] ${
        collapsed ? 'lg:w-14' : 'lg:w-60'
      }`
    : `${collapsed ? 'w-14' : 'w-60'} transition-[width] duration-200`;

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          aria-hidden="true"
          data-testid="sidebar-mobile-backdrop"
          onClick={() => setMobileOpen?.(false)}
        />
      )}
      <aside
        className={`${asideLayoutClass} bg-ms-surface border-r border-ms-border flex flex-col shrink-0`}
        data-collapsed={collapsed ? 'true' : undefined}
        data-mobile-open={mobileOpen ? 'true' : undefined}
      >
        <div
          className={`border-b border-ms-border flex items-center gap-2 ${
            collapsed ? 'flex-col px-2 py-3' : 'px-4 py-4'
          }`}
        >
          <div className="w-7 h-7 rounded bg-ms-orange-dark text-white flex items-center justify-center text-[14px] font-bold shrink-0">
            M
          </div>
          {!collapsed && (
            /* MKS-493 — the logo wordmark: `ms-logo` gives Sparkle its display
               face, and the brand emoji appears only in that theme. */
            <span className="ms-logo text-[15px] font-semibold text-ms-text-strong">
              Monksheets
              <ThemeEmoji role="brand" />
            </span>
          )}
          {/* MKS-538 — in the mobile drawer "collapse to an icon rail" is a
              desktop affordance with nothing to do, so the same slot carries a
              close button instead. */}
          {canCollapse && mobileDrawerOn && !isDesktop && (
            <button
              type="button"
              onClick={() => setMobileOpen?.(false)}
              aria-label="Close menu"
              title="Close menu"
              className="ml-auto p-1 rounded text-ms-text-muted hover:text-ms-text-strong hover:bg-ms-surface-2 focus:outline-none focus:ring-2 focus:ring-ms-orange/40"
            >
              <XMarkIcon className="w-5 h-5" aria-hidden="true" />
            </button>
          )}
          {canCollapse && !(mobileDrawerOn && !isDesktop) && (
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
              title={collapsed ? 'Expand menu' : 'Collapse menu'}
              className={`${
                collapsed ? '' : 'ml-auto'
              } p-1 rounded text-ms-text-muted hover:text-ms-text-strong hover:bg-ms-surface-2 focus:outline-none focus:ring-2 focus:ring-ms-orange/40`}
            >
              {collapsed ? (
                <ChevronDoubleRightIcon className="w-4 h-4" aria-hidden="true" />
              ) : (
                <ChevronDoubleLeftIcon className="w-4 h-4" aria-hidden="true" />
              )}
            </button>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          {renderSection('My work', myWorkItems)}

          {/* 93-02 nav map — the whole Projects block (header, Manager
              Overview, and the customer→project hierarchy) is `projects`-owned;
              `workmgmt` cannot be enabled without `projects` (DAG), so one
              module gate covers both. `useSidebarCustomers()` above stays an
              unconditional hook call — `/api/sidebar/customers` is core-owned
              (backend/src/api/routes/modules/core.ts:215) and hooks must not
              be called conditionally. */}
          {hasModule('projects') && collapsed && (
            // MKS-377 — the rail keeps Manager Overview; the customer→project tree needs the full
            // width, so its icon opens the menu instead.
            <div className="px-2 py-1" role="group" aria-label="Projects">
              {railDivider}
              {showManagerOverviewLink && (
                <Link
                  to="/manager/overview"
                  className={railItemClass(location.pathname === '/manager/overview')}
                  title="Manager Overview"
                  aria-label="Manager Overview"
                >
                  <ChartBarIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
                </Link>
              )}
              <button
                type="button"
                onClick={toggleCollapsed}
                className={railItemClass(false)}
                title="Projects — open the menu to browse"
                aria-label="Projects — open the menu to browse"
              >
                <FolderIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
              </button>
            </div>
          )}

          {hasModule('projects') && !collapsed && (
            <div className="px-3 py-2">
              {/* Petra UAT 2026-05-22 — Projects section: plain header (no link),
                  "Manager Overview" rendered as a first-class item for PM + admin,
                  every project routes to its kanban board for ALL roles (was
                  previously 'detail' for admin/PM). Phase 45 Plan 45-05 (D-09)
                  header-link replaced by item-level link. Impersonating admin →
                  effectiveFlags.isAdmin AND .isManager both false (masked by
                  !isImpersonating in effectiveFlags) — Manager Overview link does
                  NOT render, Phase 41 P15 impersonation guard preserved. */}
              <SectionHeader label="Projects" />
              {showManagerOverviewLink && (
                <Link
                  to="/manager/overview"
                  className={itemClass(location.pathname === '/manager/overview')}
                  data-testid="sidebar-projects-manager-overview"
                >
                  <ChartBarIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
                  <span className="flex-1 truncate">Manager Overview</span>
                </Link>
              )}
              <SidebarProjectsSection
                customers={customers ?? []}
                isLoading={customersLoading}
                isError={customersError}
                linkTarget={effectiveFlags.isAdmin || isPM ? 'detail' : 'board'}
              />
            </div>
          )}

          {manageVisible && collapsed && (
            <div className="px-2 py-1" role="group" aria-label="Manage">
              {railDivider}
              {approvalsAnyVisible && (
                // MKS-377 — Approvals has only children: its icon opens the menu with them shown.
                <button
                  type="button"
                  onClick={() => {
                    setApprovalsOpen(true);
                    toggleCollapsed();
                  }}
                  className={railItemClass(isApprovalsRouteActive)}
                  title={totalApprovals > 0 ? `Approvals (${totalApprovals})` : 'Approvals'}
                  aria-label="Approvals"
                >
                  <CheckCircleIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
                  <Badge count={totalApprovals} compact />
                </button>
              )}
              {manageItems.filter((i) => i.visible !== false).map(renderItem)}
            </div>
          )}

          {manageVisible && !collapsed && (
            <div className="px-3 py-2">
              <SectionHeader label="Manage" />
              {approvalsAnyVisible && (
                <>
                  <button
                    type="button"
                    onClick={() => setApprovalsOpen((v) => !v)}
                    className={`${itemClass(isApprovalsRouteActive)} w-full text-left`}
                    aria-expanded={approvalsOpen}
                  >
                    <CheckCircleIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
                    <span className="flex-1 truncate">Approvals</span>
                    <Badge count={totalApprovals} />
                    {approvalsOpen ? (
                      <ChevronDownIcon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                    ) : (
                      <ChevronRightIcon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                    )}
                  </button>
                  {approvalsOpen && (
                    <div className="ml-3 mt-0.5">{approvalsChildren.map(renderItem)}</div>
                  )}
                </>
              )}
              {manageItems.filter((i) => i.visible !== false).map(renderItem)}
            </div>
          )}

          {reportsVisible && renderSection('Reports', reportsItems)}
          {businessVisible && renderSection('Business', businessItems)}
          {financeVisible && renderSection('Finance', financeItems)}
          {ismsVisible && renderSection('ISMS', ismsItems)}
          {renderSection('HR', hrItems)}
          {systemVisible && renderSection('System', systemItems)}
        </nav>

        <div className={`border-t border-ms-border shrink-0 ${collapsed ? 'px-1 py-2 flex justify-center' : 'px-3 py-3'}`}>
          <UserMenu collapsed={collapsed} />
        </div>
      </aside>
    </>
  );
}
